import {
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  DestroyRef,
  ElementRef,
  HostListener,
  PLATFORM_ID,
  QueryList,
  TemplateRef,
  afterNextRender,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { NgTemplateOutlet, isPlatformBrowser } from '@angular/common';

import { GrEmptyStateComponent } from '@components/empty-state';

import { GrCanvasItemComponent } from '../canvas-item';
import { GrCanvasRendererDirective } from '../canvas-renderer';
import { moveItem } from '../canvas-editor/canvas-editor-grid';
import type {
  DragSession,
  GrCanvasContext,
  GrCanvasItem,
  GrCanvasLayout,
  GrCanvasMoveEvent,
  GrCanvasResizeEvent,
  ItemDragStart,
} from './canvas.types';

const EDITOR_TRAILING_ROWS = 6;

@Component({
  selector: 'gr-canvas',
  imports: [NgTemplateOutlet, GrCanvasItemComponent, GrEmptyStateComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'gr-canvas relative block w-full',
    '[class.gr-canvas--editing]': 'editing()',
    '[class.gr-canvas--dragging]': '!!drag()',
    '[style.--gr-canvas-cols]': 'effectiveCols()',
    '[style.--gr-canvas-row]': 'rowHeight()',
  },
  templateUrl: './canvas.component.html',
})
export class GrCanvasComponent {
  private readonly hostRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly containerWidth = signal<number>(0);

  private readonly gridRef = viewChild<ElementRef<HTMLElement>>('grid');

  get hostElement(): HTMLElement {
    return this.hostRef.nativeElement;
  }

  projectPoint(
    clientX: number,
    clientY: number,
    colSpan: number,
  ): { x: number; y: number; insideGrid: boolean } | null {
    const grid = this.gridRef()?.nativeElement;
    if (!grid) return null;
    const rect = grid.getBoundingClientRect();
    const style = getComputedStyle(grid);
    const gap = this.parsePx(style.columnGap || style.gap || '0');
    const cols = this.effectiveCols();
    const cellWidth = Math.max(1, (grid.clientWidth - gap * (cols - 1)) / cols);
    const rowHeight = this.parsePx(style.gridAutoRows || '5rem') || 80;
    const localX = clientX - rect.left;
    const localY = clientY - rect.top;
    const insideGrid =
      clientX >= rect.left &&
      clientX <= rect.right &&
      clientY >= rect.top &&
      clientY <= rect.bottom;
    const x = Math.min(
      Math.max(Math.round(localX / (cellWidth + gap)), 0),
      Math.max(0, cols - colSpan),
    );
    const y = Math.max(0, Math.round(localY / (rowHeight + gap)));
    return { x, y, insideGrid };
  }

  readonly items = input.required<GrCanvasLayout>();
  readonly editing = input<boolean>(false);
  readonly cols = input<number>(12);
  readonly rowHeight = input<string>('5rem');
  readonly phantom = input<GrCanvasItem | null>(null);
  readonly emptyLabel = input<string>('No widgets yet — add one to get started.');
  readonly emptyIcon = input<string>('dashboard_customize');
  readonly minSizeByKind = input<Record<string, { readonly colSpan: number; readonly rowSpan: number }>>(
    {},
  );
  readonly minSizeById = input<Record<string, { readonly colSpan: number; readonly rowSpan: number }>>({});
  readonly minRows = input<number>(0);
  readonly editorPadding = input<boolean>(false);
  readonly dragInterceptor = input<
    ((item: GrCanvasItem, event: PointerEvent) => boolean) | null
  >(null);
  readonly ghostDrag = input<boolean>(false);

  readonly moved = output<GrCanvasMoveEvent>();
  readonly resized = output<GrCanvasResizeEvent>();
  readonly editRequested = output<{ id: string; x: number; y: number }>();
  readonly removeRequested = output<string>();
  readonly deleteRowsRequested = output<{ startRow: number; count: number }>();

  @ContentChildren(GrCanvasRendererDirective, { descendants: true })
  protected readonly rendererList!: QueryList<GrCanvasRendererDirective>;

  readonly renderers = input<readonly GrCanvasRendererDirective[] | null>(null);

  protected readonly drag = signal<DragSession | null>(null);
  private autoScrollRaf: number | null = null;
  private lastPointerClientY = 0;

  protected readonly hasItems = computed<boolean>(() => this.items().length > 0);

  protected readonly emptyRowGroups = computed<ReadonlyArray<{ startRow: number; count: number }>>(() => {
    if (!this.editing() || this.scaledFromBase()) return [];
    const items = this.placedItems();
    if (items.length === 0) return [];
    const maxRow = items.reduce((m, i) => Math.max(m, (i.y ?? 0) + i.rowSpan), 0);
    const occupied = new Set<number>();
    for (const item of items) {
      const y = item.y ?? 0;
      for (let r = y; r < y + item.rowSpan; r++) occupied.add(r);
    }
    const groups: Array<{ startRow: number; count: number }> = [];
    let groupStart = -1;
    for (let r = 0; r < maxRow; r++) {
      if (!occupied.has(r)) {
        if (groupStart === -1) groupStart = r;
      } else if (groupStart !== -1) {
        groups.push({ startRow: groupStart, count: r - groupStart });
        groupStart = -1;
      }
    }
    return groups;
  });

  constructor() {
    if (!isPlatformBrowser(this.platformId) || typeof ResizeObserver === 'undefined') return;
    afterNextRender(() => {
      const observer = new ResizeObserver((entries) => {
        const w = entries[0]?.contentRect.width ?? 0;
        if (w > 0) this.containerWidth.set(w);
      });
      observer.observe(this.hostRef.nativeElement);
      this.destroyRef.onDestroy(() => observer.disconnect());
    });
  }

  protected readonly effectiveCols = computed<number>(() => {
    const base = this.cols();
    if (this.editing()) return base;
    const w = this.containerWidth();
    if (w === 0) return base;
    if (w < 30 * 16) return 1;
    if (w < 45 * 16) return 2;
    if (w < 60 * 16) return 4;
    if (w < 80 * 16) return 8;
    return base;
  });

  protected readonly scaledFromBase = computed<boolean>(
    () => this.effectiveCols() < this.cols(),
  );

  protected readonly placedItems = computed<ReadonlyArray<GrCanvasItem>>(() =>
    this.items().map((item) => ({ ...item, x: item.x ?? 0, y: item.y ?? 0 })),
  );

  protected readonly contentBottom = computed<number>(() =>
    this.placedItems().reduce((m, i) => Math.max(m, (i.y ?? 0) + i.rowSpan), 0),
  );

  private readonly baseBottom = computed<number>(() =>
    Math.max(this.contentBottom(), this.minRows()),
  );

  private readonly showEditorPad = computed<boolean>(
    () => this.editing() && this.editorPadding(),
  );

  protected readonly canvasBottom = computed<number>(() =>
    this.showEditorPad() ? this.baseBottom() + EDITOR_TRAILING_ROWS : this.baseBottom(),
  );

  protected readonly editorPadRow = computed<string | null>(() => {
    if (!this.showEditorPad()) return null;
    return `${this.baseBottom() + 1} / span ${EDITOR_TRAILING_ROWS}`;
  });

  maxDropY(rowSpan: number): number {
    return Math.max(0, this.canvasBottom() - rowSpan);
  }

  protected readonly displayItems = computed<ReadonlyArray<GrCanvasItem>>(() => {
    const session = this.drag();
    if (!session) return this.placedItems();
    return this.ghostDrag() ? this.placedItems() : session.preview;
  });

  protected readonly dragMinHeight = computed<number | null>(
    () => this.drag()?.pinnedHeight ?? null,
  );

  protected readonly placeholder = computed<GrCanvasItem | null>(() => {
    const session = this.drag();
    if (!session) return null;
    return session.preview.find((item) => item.id === session.id) ?? null;
  });

  protected templateFor(kind: string): TemplateRef<GrCanvasContext> | null {
    const list = this.renderers() ?? this.rendererList?.toArray() ?? [];
    const match = list.find((r) => r.kind() === kind);
    return match?.templateRef ?? null;
  }

  protected contextFor(item: GrCanvasItem, index: number): GrCanvasContext {
    return { $implicit: item, item, index, editing: this.editing() };
  }

  protected minColFor(kind: string, id: string): number {
    return this.minSizeById()[id]?.colSpan ?? this.minSizeByKind()[kind]?.colSpan ?? 1;
  }

  protected minRowFor(kind: string, id: string): number {
    return this.minSizeById()[id]?.rowSpan ?? this.minSizeByKind()[kind]?.rowSpan ?? 1;
  }

  protected effectiveColSpan(item: GrCanvasItem): number {
    const base = this.cols();
    const effective = this.effectiveCols();
    if (effective >= base) return Math.min(item.colSpan, effective);
    if (effective <= 4 && item.colSpan >= base / 3) return effective;
    const scaled = Math.round((item.colSpan * effective) / base);
    return Math.min(Math.max(scaled, 1), effective);
  }

  protected isDragged(id: string): boolean {
    return !this.ghostDrag() && this.drag()?.id === id;
  }

  protected isGhost(id: string): boolean {
    return this.ghostDrag() && this.drag()?.id === id;
  }

  protected floatRectFor(
    id: string,
  ): { left: number; top: number; width: number; height: number } | null {
    const session = this.drag();
    if (!session || session.id !== id) return null;
    return {
      left: session.floatLeft,
      top: session.floatTop,
      width: session.width,
      height: session.height,
    };
  }

  protected trackById(_index: number, item: GrCanvasItem): string {
    return item.id;
  }

  protected onItemDragStart(payload: ItemDragStart): void {
    if (!this.editing() || this.scaledFromBase()) return;
    const grid = this.gridRef()?.nativeElement;
    if (!grid) return;

    const event = payload.event;
    const item = this.placedItems().find((i) => i.id === payload.id);
    if (!item) return;

    const interceptor = this.dragInterceptor();
    if (interceptor?.(item, event)) return;

    const gridRect = grid.getBoundingClientRect();
    const gridStyle = getComputedStyle(grid);
    const gap = this.parsePx(gridStyle.columnGap || gridStyle.gap || '0');
    const cols = this.effectiveCols();
    const cellWidth = Math.max(1, (grid.clientWidth - gap * (cols - 1)) / cols);
    const rowHeight = this.parsePx(gridStyle.gridAutoRows || '5rem') || 80;

    const hostRect = payload.host.getBoundingClientRect();
    const scrollEl = this.findScrollableAncestor(grid);
    const startScrollTop = scrollEl?.scrollTop ?? 0;
    const startMaxScrollTop = scrollEl
      ? Math.max(startScrollTop, scrollEl.scrollHeight - scrollEl.clientHeight)
      : startScrollTop;

    event.preventDefault();
    (event.target as Element).setPointerCapture?.(event.pointerId);
    this.lastPointerClientY = event.clientY;

    this.drag.set({
      id: payload.id,
      pointerId: event.pointerId,
      base: this.placedItems(),
      colSpan: item.colSpan,
      rowSpan: item.rowSpan,
      grabOffsetX: event.clientX - hostRect.left,
      grabOffsetY: event.clientY - hostRect.top,
      cellWidth,
      rowHeight,
      gap,
      pinnedHeight: gridRect.height,
      scrollEl,
      startMaxScrollTop,
      floatLeft: hostRect.left,
      floatTop: hostRect.top,
      width: hostRect.width,
      height: hostRect.height,
      targetX: item.x ?? 0,
      targetY: item.y ?? 0,
      preview: this.placedItems(),
    });
  }

  @HostListener('document:pointermove', ['$event'])
  protected onPointerMove(event: PointerEvent): void {
    const session = this.drag();
    if (!session || event.pointerId !== session.pointerId) return;
    this.lastPointerClientY = event.clientY;
    this.applyDrag(session, event.clientX, event.clientY);
    this.ensureAutoScroll();
  }

  @HostListener('document:pointerup', ['$event'])
  @HostListener('document:pointercancel', ['$event'])
  protected onPointerUp(event: PointerEvent): void {
    const session = this.drag();
    if (!session || event.pointerId !== session.pointerId) return;
    this.stopAutoScroll();
    this.drag.set(null);
    this.moved.emit({ id: session.id, x: session.targetX, y: session.targetY });
  }

  private applyDrag(session: DragSession, clientX: number, clientY: number): void {
    const grid = this.gridRef()?.nativeElement;
    if (!grid) return;
    const gridRect = grid.getBoundingClientRect();
    const floatLeft = clientX - session.grabOffsetX;
    const floatTop = clientY - session.grabOffsetY;

    const cols = this.effectiveCols();
    const localX = floatLeft - gridRect.left;
    const localY = floatTop - gridRect.top;
    const targetX = Math.min(
      Math.max(Math.round(localX / (session.cellWidth + session.gap)), 0),
      Math.max(0, cols - session.colSpan),
    );
    const targetY = Math.min(
      Math.max(0, Math.round(localY / (session.rowHeight + session.gap))),
      this.maxDropY(session.rowSpan),
    );

    const preview = moveItem(session.base, session.id, targetX, targetY, cols);

    this.drag.set({
      ...session,
      floatLeft,
      floatTop,
      targetX,
      targetY,
      preview,
    });
  }

  private ensureAutoScroll(): void {
    if (this.autoScrollRaf !== null) return;
    const tick = () => {
      const session = this.drag();
      const scrollEl = session?.scrollEl;
      if (!session || !scrollEl) {
        this.autoScrollRaf = null;
        return;
      }
      const rect =
        scrollEl === document.scrollingElement || scrollEl === document.documentElement
          ? { top: 0, bottom: window.innerHeight }
          : scrollEl.getBoundingClientRect();
      const edge = 56;
      const maxSpeed = 18;
      const fromBottom = rect.bottom - this.lastPointerClientY;
      const fromTop = this.lastPointerClientY - rect.top;
      let speed = 0;
      if (fromBottom < edge) speed = maxSpeed * (1 - Math.max(0, fromBottom) / edge);
      else if (fromTop < edge) speed = -maxSpeed * (1 - Math.max(0, fromTop) / edge);
      if (speed !== 0) {
        const before = scrollEl.scrollTop;
        const maxScrollTop = scrollEl.scrollHeight - scrollEl.clientHeight;
        const next =
          speed > 0 ? Math.min(before + speed, maxScrollTop) : Math.max(before + speed, 0);
        if (next !== before) {
          scrollEl.scrollTop = next;
          this.applyDrag(
            session,
            session.floatLeft + session.grabOffsetX,
            this.lastPointerClientY,
          );
        }
      }
      this.autoScrollRaf = requestAnimationFrame(tick);
    };
    this.autoScrollRaf = requestAnimationFrame(tick);
  }

  private stopAutoScroll(): void {
    if (this.autoScrollRaf !== null) {
      cancelAnimationFrame(this.autoScrollRaf);
      this.autoScrollRaf = null;
    }
  }

  private findScrollableAncestor(start: HTMLElement): HTMLElement {
    let el: HTMLElement | null = start.parentElement;
    while (el && el !== document.body && el !== document.documentElement) {
      const oy = getComputedStyle(el).overflowY;
      if (oy === 'auto' || oy === 'scroll') return el;
      el = el.parentElement; 
    }
    return document.documentElement;
  }

  private parsePx(value: string): number {
    if (!value) return 0;
    if (value.endsWith('px')) return parseFloat(value);
    if (value.endsWith('rem')) {
      const root = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      return parseFloat(value) * root;
    }
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : 0;
  }
}
